import {
  Component,
  Input,
  ViewEncapsulation,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  Renderer2
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WindowService } from 'src/shared/providers/window.service';
import { LoadingStateService } from '../../../providers/loading-state/loading-state.service';

@Component({
    selector: 'tidy-message-input',
    templateUrl: 'message-input.html',
    styleUrls: ['./message-input.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MessageInputComponent),
            multi: true
        }
    ],
    standalone: false
})
export class MessageInputComponent implements ControlValueAccessor, OnInit {
  @ViewChild('inputElement') inputElement: ElementRef;

  @Input() fixedWidth = true;
  @Input() hasAttachment = false;
  @Input() icon = 'assets/svg/search.svg';
  @Input() isSendingMessage = false;
  @Input() leftIcon = 'assets/svg/attach-outline.svg';
  @Input() leftIconAction = null;
  @Input() placeholder = 'Send Message';
  @Input() rightIconAction = null;
  @Input() showBlackButtons: boolean;
  @Input() showLoadingOverlay = true;
  @Input() speakMode = false;
  @Input() isVoiceActive = false;
  @Output() speakClick = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<string>();

  value = '';
  isInputEmpty = true;
  onChange: any = () => {
    // Placeholder for ControlValueAccessor
  };
  onTouched: any = () => {
    // Placeholder for ControlValueAccessor
  };
  actionLoading = false;

  constructor(
    private el: ElementRef,
    public loadingState: LoadingStateService,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {}

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
  }

  writeValue(value: string): void {
    this.value = value;
    this.isInputEmpty = !value;
    if (value) {
      setTimeout(() => {
        const textarea = this.inputElement?.nativeElement;
        if (textarea) {
          textarea.selectionStart = value.length;
          textarea.selectionEnd = value.length;
        }
        this.adjustTextareaHeight();
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(value: string): void {
    this.value = value;
    this.isInputEmpty = !value;
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
    this.adjustTextareaHeight();
  }

  adjustTextareaHeight(): void {
    const textarea = this.inputElement?.nativeElement;
    if (!textarea) {return;}
    textarea.style.height = '24px';
    textarea.style.overflowY = 'hidden';
    if (textarea.scrollHeight > textarea.clientHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = 'auto';
    }
  }

  clearInput(): void {
    this.value = '';
    this.isInputEmpty = true;
    this.onChange('');
    this.onTouched();
    this.valueChange.emit('');
    const textarea = this.inputElement?.nativeElement;
    if (textarea) {
      textarea.style.height = '24px';
      textarea.style.overflowY = 'hidden';
    }
  }

  executeSpeakAction(): void {
    this.speakClick.emit();
  }

  async executeRightIconAction() {
    if (this.loadingState.loading || !this.rightIconAction || (this.isInputEmpty && !this.hasAttachment)) {
      return;
    }
    try {
      if (this.showLoadingOverlay) {
        this.loadingState.loading = true;
      }
      this.actionLoading = true;
      await this.rightIconAction();
      this.clearInput();
    } catch (_err) {
      // Silent error handling - action failure is not critical
    } finally {
      if (this.showLoadingOverlay) {
        this.loadingState.loading = false;
      }
      this.actionLoading = false;
    }
  }

  async executeLeftIconAction() {
    if (this.loadingState.loading || !this.leftIconAction) {
      return;
    }
    try {
      if (this.showLoadingOverlay) {
        this.loadingState.loading = true;
      }
      await this.leftIconAction();
    } catch (_err) {
      // Silent error handling - action failure is not critical
    } finally {
      if (this.showLoadingOverlay) {
        this.loadingState.loading = false;
      }
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && this.rightIconAction && (!this.isInputEmpty || this.hasAttachment) && !this.loadingState.loading) {
      event.preventDefault();
      this.executeRightIconAction();
    }
  }

  focusInput() {
    setTimeout(() => {
      this.inputElement?.nativeElement?.focus();
    });
  }

  blurInput() {
    this.inputElement?.nativeElement?.blur();
  }
}
